import { type ArtefactKind, artefactKindSchema, isToolPart, toolNameOf } from "@esi/shared";

/**
 * The artefact tool parts of a message as the card reads them (openspec change
 * `artefacts-viewer`, design §2). A stored part carries the handle, version, title and kind; a
 * streaming one, on the creator's client, carries the tool input as it is written, of which only
 * the title is ever read (the content is never previewed).
 */

export const ARTEFACT_TOOLS: ReadonlySet<string> = new Set(["create_artefact", "update_artefact"]);

export function isArtefactToolPart(part: unknown): boolean {
  return isToolPart(part) && ARTEFACT_TOOLS.has(toolNameOf(part as never));
}

export type ArtefactCardState =
  | { status: "writing"; title: string | null; handle: string | null }
  | { status: "failed"; title: string | null }
  | { status: "done"; handle: string; version: number; title: string; kind: ArtefactKind };

const str = (value: unknown, max = 200): string | null =>
  typeof value === "string" && value.trim() !== "" ? value.slice(0, max) : null;

/** What the card shows for one artefact tool part. */
export function artefactCardOf(part: unknown): ArtefactCardState {
  const p = part as { state?: unknown; input?: unknown; output?: unknown };
  const input = (typeof p.input === "object" && p.input !== null ? p.input : {}) as {
    title?: unknown;
    artefact?: unknown;
  };
  const output = (typeof p.output === "object" && p.output !== null ? p.output : {}) as {
    artefact?: unknown;
    version?: unknown;
    title?: unknown;
    kind?: unknown;
    error?: unknown;
  };
  if (p.state === "input-streaming" || p.state === "input-available") {
    return { status: "writing", title: str(input.title), handle: str(input.artefact, 16) };
  }
  const kind = artefactKindSchema.safeParse(output.kind);
  if (
    p.state === "output-available" &&
    typeof output.artefact === "string" &&
    /^A[1-9][0-9]*$/.test(output.artefact) &&
    typeof output.version === "number" &&
    Number.isInteger(output.version) &&
    output.version > 0 &&
    kind.success
  ) {
    return {
      status: "done",
      handle: output.artefact,
      version: output.version,
      title: str(output.title) ?? output.artefact,
      kind: kind.data,
    };
  }
  return { status: "failed", title: str(input.title) };
}
